import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ExecutionResultService, ExecutionResultRecord } from './execution-result.service';
import { SessionPoolService } from './session-pool.service';
import { SessionState } from '../shared/models';

export interface TestStep {
  id?: string;
  stepUuid?: string;  // UUID from StepObject (database step ID)
  action: 'fill' | 'click' | 'check' | 'uncheck' | 'select' | 'assert' | 'verify' | 'verifyNotExist' | 'assertNotExist' | 'getText' | 'hover' | 'wait' | 'navigate' | 'pressEnter' | 'setDate' | 'fillDate' | 'verifyTable' | 'verifyTableCell' | 'verifyTableRows' | 'verifyTableColumn' | 'verifyTableColumns' | 'verifyTableMultiple' | 'verifyTableNotContains' | 'verifyTableNotInColumn';
  selector: {
    automationId?: string;
    testAttr?: { name: string; value: string };
    id?: string;
    css?: string;
    role?: string;
    name?: string;
    // Table validation properties
    tableValidation?: 'contains' | 'rowCount' | 'notContains' | 'notInColumn';
    tableSelector?: string; // For verifyTableMultiple - specific table identifier
    validations?: Array<{ columnPairs: Array<{ column: string; value: string; operator?: 'equals' | 'contains' | 'notEquals' | 'notContains' | 'greaterThan' | 'lessThan' | 'startsWith' | 'endsWith' | 'isEmpty' | 'isNotEmpty' }> }>; // For verifyTableMultiple - array of validations
    rowIndex?: number;
    columnIdentifier?: string;
  operator?: 'equals' | 'contains' | 'containsAll' | 'is';
    searchAllPages?: boolean; // Enable automatic pagination search
    validateAllRecords?: boolean; // Validate ALL rows in column (not just first match)
    columnPairs?: {column: string, value: string, operator?: 'equals' | 'contains' | 'notEquals' | 'notContains' | 'greaterThan' | 'lessThan' | 'startsWith' | 'endsWith' | 'isEmpty' | 'isNotEmpty'}[]; // For multi-column validation
    // Not-exist validation properties
    expectAbsent?: boolean; // If true, validation succeeds when element is not found
    absenceTimeout?: number; // Custom timeout for absence checks (default: 3000ms vs 10000ms for presence)
  };
  value?: string;
  // For masking sensitive values in UI while keeping real value for execution
  displayValue?: string;
  text?: string;
  timeout?: number;
  description?: string;
  optional?: boolean;  // If true, step failure won't stop execution
  // Edit tracking
  modified?: boolean;                    // true if the step was edited after recording
  modifiedFields?: string[];             // which fields were edited (e.g., ['text','value','selector'])
  lastModifiedAt?: string;               // ISO timestamp of last edit
}

export interface ExecutionResult {
  success: boolean;
  passed?: boolean;
  result?: any;
  error?: string;
  timestamp: string;
  duration?: number;
  screenshot?: string;  // Screenshot captured during execution (base64 data URL)
  assertion?: {
    type?: string;
    passed?: boolean;
    [key: string]: any;
  };
}

export interface StepExecution {
  step: TestStep;
  result?: ExecutionResult;
  status: 'pending' | 'running' | 'success' | 'failed' | 'stopped';
}

export interface ScenarioExecutionReport {
  totalSteps: number;
  passed: number;
  failed: number;
  stopped: number;
  duration: number;
  steps: StepExecution[];
}

// Step metadata for mapping step UUIDs to step descriptions
export interface StepMetadata {
  stepId: number;
  stepUuid: string;
  stepDescription: string;
  isRecording?: boolean;  // Flag indicating if this is a recorded step
  additionalInfo?: any;   // Parsed additional info containing recorded step data
}

@Injectable({
  providedIn: 'root'
})
export class TestExecutorService {
  private executionHistory$ = new BehaviorSubject<StepExecution[]>([]);
  private currentStep$ = new BehaviorSubject<TestStep | null>(null);
  
  // Current session for execution (optional - if set, uses session windows instead of hidden host)
  private currentSession: SessionState | null = null;

  constructor(
    private executionResultService: ExecutionResultService,
    private sessionPoolService: SessionPoolService
  ) { }

  get executionHistory(): Observable<StepExecution[]> {
    return this.executionHistory$.asObservable();
  }

  get currentStep(): Observable<TestStep | null> {
    return this.currentStep$.asObservable();
  }

  /**
   * Set the current session for execution
   * When set, executeStep will use session windows instead of hidden host
   */
  setSession(session: SessionState | null): void {
    console.log('[TestExecutor] Setting session:', session ? session.windowHandle : 'none');
    this.currentSession = session;
  }

  /**
   * Get current session
   */
  getSession(): SessionState | null {
    return this.currentSession;
  }

  /**
   * Parse natural language step into executable TestStep
   * Examples:
   * - "Enter username in textbox" -> fill action with text matching
   * - "Enter test in username" -> fill action (reversed order)
   * - "Enter username as test" -> fill action (alternate syntax)
   * - "Click login button" -> click action
   * - "Check remember me checkbox" -> check action
   * - "Verify page contains Welcome" -> assert action
   */
  parseNaturalLanguageStep(input: string, metadata?: any): TestStep | null {
    const normalized = input.toLowerCase().trim();
    const original = input.trim(); // Keep original for extracting case-sensitive values
        
    // Fill/Type patterns - Handle multiple formats
    // Format 1: "Enter <value> in/into <field>" - e.g., "Enter Test@123 in password"
    // CRITICAL FIX: Use more specific pattern to handle field names containing "in"
    // Changed from .+? to [^]+ to match up to LAST occurrence of " in " or " into "
    // This handles: "Enter Accounted in the Supplier Contract Status" correctly
    if (normalized.match(/^(enter|type|fill|input)\s+(.+)\s+(in|into)\s+(?:the\s+)?(.+?)$/)) {
      // Use original input to extract case-sensitive value
      const originalMatch = original.match(/^(enter|type|fill|input)\s+(.+)\s+(in|into)\s+(?:the\s+)?(.+?)$/i);
      const normalizedMatch = normalized.match(/^(enter|type|fill|input)\s+(.+)\s+(in|into)\s+(?:the\s+)?(.+?)$/);
      
      if (originalMatch && normalizedMatch) {
        let value = originalMatch[2].trim(); // Case-sensitive value from original
        let fieldName = normalizedMatch[4].trim(); // Lowercase field name for matching
        
        // CRITICAL FIX: If value contains " in " or " into ", split at the LAST occurrence
        // This handles cases like "Enter Accounted in Supplier Contract Status"
        // where "Accounted" is the value and "Supplier Contract Status" is the field
        const lastInMatch = value.match(/^(.+)\s+(in|into)$/i);
        if (lastInMatch) {
          // Value incorrectly captured "Accounted in", extract just "Accounted"
          value = lastInMatch[1].trim();
          // Field name needs to prepend what was in the value
          // Actually, let's re-parse the whole thing differently
          
          // Find the last occurrence of " in " or " into " in the normalized string
          const lastInIndex = Math.max(
            normalized.lastIndexOf(' in '),
            normalized.lastIndexOf(' into ')
          );
          
          if (lastInIndex > 0) {
            // Split at the last occurrence
            const beforeIn = original.substring(0, lastInIndex).trim();
            const afterIn = original.substring(lastInIndex).trim();
            
            // Extract value from beforeIn (remove the command word)
            const valueMatch = beforeIn.match(/^(enter|type|fill|input)\s+(.+)$/i);
            if (valueMatch) {
              value = valueMatch[2].trim();
            }
            
            // Extract field from afterIn (remove "in" or "into" and optional "the")
            const fieldMatch = afterIn.match(/^(in|into)\s+(?:the\s+)?(.+)$/i);
            if (fieldMatch) {
              fieldName = fieldMatch[2].toLowerCase().trim();
            }
          }
        }
        
        // Extract key identifier (e.g., "username" from "username textbox")
        const keyWord = fieldName.split(/\s+/)[0]; // First word is usually the key identifier
        
        // CRITICAL FIX: Check if precondition logic replaced value with placeholder
        // Only check originalValue (set by precondition changes), NOT additionalInfo.value
        let finalValue = value;
        const isPlaceholder = value && value.startsWith('{{') && value.endsWith('}}');
        const isSensitiveField = fieldName.includes('password') || fieldName.includes('username');
        
        // Only recover if value is a placeholder AND originalValue exists (from precondition modification)
        if (isPlaceholder && metadata?.additionalInfo?.originalValue && !metadata?.additionalInfo?.recordedStep) {
          finalValue = metadata.additionalInfo.originalValue;
        }
              
        return {
          action: 'fill',
          selector: {
            role: 'textbox',
            name: fieldName, // Use FULL field name for better matching (changed from keyWord)
            id: keyWord    // Also try as ID with first word
          },
          value: finalValue, // Use preserved value if available, otherwise parsed value
          text: fieldName, // Keep full text for matching
          description: original
        };
      }
    }
    
    // Format 2: "Enter <field> as <value>" - e.g., "Enter password as Test@123"
    if (normalized.match(/^(enter|type|fill|input)\s+(.+?)\s+as\s+(.+?)$/)) {
      // Use original input to extract case-sensitive value
      const originalMatch = original.match(/^(enter|type|fill|input)\s+(.+?)\s+as\s+(.+?)$/i);
      const normalizedMatch = normalized.match(/^(enter|type|fill|input)\s+(.+?)\s+as\s+(.+?)$/);

      // Field name (lowercased), strip leading 'the '
      const fieldNameRaw = normalizedMatch![2];
      const fieldName = fieldNameRaw.replace(/^\s*the\s+/, '').trim();
      // Original-case field name for display/matching, also strip leading 'the '
      const originalFieldRaw = originalMatch![2];
      const originalField = originalFieldRaw.replace(/^\s*the\s+/i, '').trim();
      const value = originalMatch![3]; // Case-sensitive value from original

      // Extract key identifier
      const keyWord = fieldName.split(/\s+/)[0];
      
      // CRITICAL FIX: Check if precondition logic replaced value with placeholder
      // Only check originalValue (set by precondition changes), NOT additionalInfo.value
      let finalValue = value;
      const isPlaceholder = value && value.startsWith('{{') && value.endsWith('}}');
      
      // Only recover if value is a placeholder AND originalValue exists (from precondition modification)
      if (isPlaceholder && metadata?.additionalInfo?.originalValue && !metadata?.additionalInfo?.recordedStep) {
        console.log(`[TestExecutor] Recovering originalValue: placeholder detected`);
        finalValue = metadata.additionalInfo.originalValue;
      }

      return {
        action: 'fill',
        selector: {
          role: 'textbox',
          name: fieldName,
          id: keyWord
        },
        value: finalValue, // Use preserved value if available, otherwise parsed value
        text: originalField,
        description: original
      };
    }
    
    // Date patterns - "Set <field> to <date>" or "Fill <field> with <date>"
    // Examples: "Set Purchase Order Date to Today", "Set Effective Date to 11/10/1992"
    // Supports: today, tomorrow, yesterday, now, +N days, -N days, or specific dates
    if (normalized.match(/^(set|fill|enter)\s+(.+?)\s+(to|with|as)\s+(.+?)$/)) {
      const originalMatch = original.match(/^(set|fill|enter)\s+(.+?)\s+(to|with|as)\s+(.+?)$/i);
      const normalizedMatch = normalized.match(/^(set|fill|enter)\s+(.+?)\s+(to|with|as)\s+(.+?)$/);
      
      if (originalMatch && normalizedMatch) {
        const fieldName = normalizedMatch![2].trim(); // Lowercase field name for matching
        const dateValue = originalMatch![4].trim(); // Case-sensitive date value
        
        // Check if the field name contains "date" or if the value looks like a date/relative date
        const isDateField = /date|time|day|month|year/i.test(fieldName);
        const isDateValue = /^(today|tomorrow|yesterday|now|[+\-]\s*\d+\s*(day|days|d))$/i.test(dateValue.trim()) ||
                          /^\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}$/.test(dateValue.trim()) ||
                          /^\d{4}[\/\-]\d{1,2}[\/\-]\d{1,2}$/.test(dateValue.trim());
        
        // If it's a date field OR date-like value, use setDate action
        if (isDateField || isDateValue) {
          // Extract key identifier from field name
          const keyWord = fieldName.split(/\s+/)[0];
          
          return {
            action: 'setDate',
            selector: {
              name: fieldName,  // Use full field name for better matching
              id: keyWord       // Also try as ID with first word
            },
            value: dateValue,   // Preserve original case for date value!
            text: fieldName,
            description: original
          };
        }
      }
    }
    
    // Press Enter Key patterns - MUST come before click patterns to avoid "press" matching click!
    // "Press Enter Key" or "Hit Enter" or "Enter Key" with optional field specification
    if (normalized.match(/^(press enter key|hit enter key|press enter|hit enter)(?:\s+(?:on|in)\s+(.+))?$/) ||
        normalized.match(/^(enter key)(?:\s+(?:on|in)\s+(.+))?$/)) {
      const match = normalized.match(/^(press enter key|hit enter key|press enter|hit enter|enter key)(?:\s+(?:on|in)\s+(.+))?$/);
      const fieldName = match![2];
            
      return {
        action: 'pressEnter',
        selector: fieldName ? { name: fieldName } : {},
        description: original
      };
    }
    
    // Click patterns - Extract clean target text
    if (normalized.match(/^(click|press|tap)\s+(.+?)$/)) {
      let elementName = normalized.match(/^(click|press|tap)\s+(.+?)$/)![2];
      
      // Clean up common phrases
      // Remove "on the", "on", "the" at the beginning
      elementName = elementName.replace(/^(?:on\s+)?(?:the\s+)?/, '');
      
      // Extract quoted text if present (highest priority)
      const quotedMatch = original.match(/['"]([^'"]+)['"]/);
      if (quotedMatch) {
        elementName = quotedMatch[1].toLowerCase();
      }
      
      // Remove "button", "link", "icon" at the end (but keep for context)
      const cleanName = elementName.replace(/\s+(button|link|icon|element)$/i, '').trim();

      return {
        action: 'click',
        selector: {
          name: cleanName  // Use cleaned name for matching
        },
        text: cleanName,  // Use cleaned name, not full command
        description: original
      };
    }
    
    // "Select X option/choice/item in/from Y" - for dropdown options within a dropdown
    // Examples: "Select Python option in Language", "Select Advanced option in Level"
    // This MUST come before "Select X option" pattern to match the more specific case first!
    if (normalized.match(/^select\s+(.+?)\s+(option|choice|item)\s+(in|from)\s+(.+?)$/)) {
      const originalMatch = original.match(/^select\s+(.+?)\s+(option|choice|item)\s+(in|from)\s+(.+?)$/i);
      const normalizedMatch = normalized.match(/^select\s+(.+?)\s+(option|choice|item)\s+(in|from)\s+(.+?)$/);
      
      const optionValue = originalMatch![1]; // Case-sensitive option value
      const dropdownName = normalizedMatch![4]; // Lowercase dropdown name for matching
      
      return {
        action: 'select',
        selector: {
          role: 'combobox',
          name: dropdownName
        },
        value: optionValue, // The option to select (preserve original case!)
        text: dropdownName,
        description: original
      };
    }
    
    // "Select X option/choice/item" should be treated as a click action for dropdown options
    // This matches only when there's NO "in/from Y" part
    if (normalized.match(/^select\s+(.+?)\s+(option|choice|item)$/)) {
      const match = normalized.match(/^select\s+(.+?)\s+(option|choice|item)$/);
      const elementName = match![1];
            
      return {
        action: 'click',
        selector: {
          name: elementName
        },
        text: elementName,
        description: original
      };
    }
    
    if (normalized.match(/^(uncheck|deselect|disable)\s+(.+?)$/)) {
      const match = normalized.match(/^(uncheck|deselect|disable)\s+(.+?)$/);
      const elementName = match![2];
      
      return {
        action: 'uncheck',
        selector: {
          role: 'checkbox',
          name: elementName
        },
        text: elementName,
        description: original
      };
    }
    
    // Check/Enable patterns for checkboxes
    // This comes AFTER all select patterns to avoid conflicts
    // CRITICAL FIX: Don't match if command contains "in" or "from" (those are fill/select patterns)
    // "Select Accounted in Supplier Contract Status" should NOT match this - it's a fill action!
    if (normalized.match(/^(check|enable|select)\s+(.+?)$/) && 
        !normalized.match(/\s+(in|from|into)\s+/)) {
      const match = normalized.match(/^(check|enable|select)\s+(.+?)$/);
      const elementName = match![2];
      
      return {
        action: 'check',
        selector: {
          role: 'checkbox',
          name: elementName
        },
        text: elementName,
        description: original
      };
    }
    
    // Select dropdown patterns - Preserve case for option value
    if (normalized.match(/^select\s+(.+?)\s+from\s+(.+?)$/)) {
      const originalMatch = original.match(/^select\s+(.+?)\s+from\s+(.+?)$/i);
      const normalizedMatch = normalized.match(/^select\s+(.+?)\s+from\s+(.+?)$/);
      
      const optionValue = originalMatch![1]; // Case-sensitive option value
      const dropdownName = normalizedMatch![2]; // Lowercase dropdown name for matching
      
      return {
        action: 'select',
        selector: {
          role: 'combobox',
          name: dropdownName
        },
        value: optionValue, // Preserve original case!
        text: dropdownName,
        description: original
      };
    }
    
    // Negative assertion patterns - "does not exist", "is not present", "should not be visible"
    if (normalized.match(/^(verify|assert|check that)\s+(.+?)\s+(does not exist|is not present|is not visible|should not exist|should not be present|is absent|not exists?)$/)) {
      const originalMatch = original.match(/^(verify|assert|check that)\s+(.+?)\s+(does not exist|is not present|is not visible|should not exist|should not be present|is absent|not exists?)$/i);
      const normalizedMatch = normalized.match(/^(verify|assert|check that)\s+(.+?)\s+(does not exist|is not present|is not visible|should not exist|should not be present|is absent|not exists?)$/);
      
      const actionVerb = normalizedMatch![1];
      const target = normalizedMatch![2].trim();
      const action = actionVerb === 'assert' ? 'assertNotExist' : 'verifyNotExist';
      
      return {
        action: action as 'verifyNotExist' | 'assertNotExist',
        selector: {
          name: target,
          expectAbsent: true,
          absenceTimeout: 3000 // Default 3s for absence checks
        },
        value: '', // No value needed for existence check
        text: target,
        description: original
      };
    }
    
    // Page-level negative assertion - "page does not contain"
    if (normalized.match(/^(verify|assert|check that)\s+page\s+(does not contain|should not contain|doesn't contain)\s+(.+?)$/)) {
      const originalMatch = original.match(/^(verify|assert|check that)\s+page\s+(does not contain|should not contain|doesn't contain)\s+(.+?)$/i);
      const expectedText = originalMatch![3].trim();
      
      return {
        action: 'verifyNotExist',
        selector: {
          css: 'body',
          expectAbsent: true
        },
        value: expectedText,
        text: 'page-content',
        description: original
      };
    }
    
    // Assertion patterns - Preserve case for expected values
    if (normalized.match(/^(verify|assert|check that)\s+(.+?)\s+(contains|equals|is)\s+(.+?)$/)) {
      const originalMatch = original.match(/^(verify|assert|check that)\s+(.+?)\s+(contains|equals|is)\s+(.+?)$/i);
      const normalizedMatch = normalized.match(/^(verify|assert|check that)\s+(.+?)\s+(contains|equals|is)\s+(.+?)$/);
      
      const target = normalizedMatch![2].trim(); // Lowercase target for matching
      const operator = normalizedMatch![3]; // contains, equals, is
      const expectedValue = originalMatch![4].trim(); // Case-sensitive expected value
      
      // Special handling for "page" and "title" - these are not elements
      if (target === 'page' || target === 'page content') {
        return {
          action: 'verify',
          selector: {
            css: 'body' // Special marker for page content check
          },
          value: expectedValue, // Preserve original case!
          text: 'page-content', // Special text marker
          description: original
        };
      }
      
      if (target === 'title' || target === 'page title') {
        return {
          action: 'verify',
          selector: {
            css: 'title' // Special marker for title check
          },
          value: expectedValue, // Preserve original case!
          text: 'page-title', // Special text marker
          description: original
        };
      }
      
      // Regular element assertion
      return {
        action: 'assert',
        selector: {
          name: target
        },
        value: expectedValue, // Preserve original case!
        text: target,
        description: original
      };
    }
    
    // Wait patterns
    if (normalized.match(/^(wait|pause)\s+(?:for\s+)?(\d+)\s*(seconds?|ms|milliseconds?)?$/)) {
      const match = normalized.match(/^(wait|pause)\s+(?:for\s+)?(\d+)\s*(seconds?|ms|milliseconds?)?$/);
      const duration = parseInt(match![2]);
      const unit = match![3] || 'seconds';
      const milliseconds = unit.startsWith('s') ? duration * 1000 : duration;
      
      return {
        action: 'wait',
        selector: {},
        value: milliseconds.toString(),
        description: original
      };
    }
    
    // Navigate patterns - URLs are case-sensitive!
    if (normalized.match(/^(navigate to|go to|open)\s+(.+)$/)) {
      const originalMatch = original.match(/^(navigate to|go to|open)\s+(.+)$/i);
      const url = originalMatch![2].trim(); // Preserve case-sensitive URL
      
      return {
        action: 'navigate',
        selector: {},
        value: url, // Preserve original case for URLs!
        description: original
      };
    }
    
    // Table validation patterns
    // Negative Pattern 1: "Verify table does not contain <text>" across all pages
    if (normalized.match(/^(verify|check|assert)\s+table\s+(does not contain|doesn't contain|should not contain)\s+(.+?)(?:\s+across\s+all\s+pages)?$/)) {
      const originalMatch = original.match(/^(verify|check|assert)\s+table\s+(does not contain|doesn't contain|should not contain)\s+(.+?)(?:\s+across\s+all\s+pages)?$/i);
      const searchText = originalMatch![3].trim();
      const searchAllPages = /across\s+all\s+pages/i.test(original);
      
      return {
        action: 'verifyTableNotContains',
        selector: { tableValidation: 'notContains', searchAllPages },
        value: searchText,
        description: original
      };
    }
    
    // Negative Pattern 2: "Verify column <name> does not contain <value>"
    if (normalized.match(/^(verify|check|assert)\s+column\s+(.+?)\s+(does not contain|doesn't contain|should not contain)\s+(.+?)(?:\s+across\s+all\s+pages)?$/)) {
      const originalMatch = original.match(/^(verify|check|assert)\s+column\s+(.+?)\s+(does not contain|doesn't contain|should not contain)\s+(.+?)(?:\s+across\s+all\s+pages)?$/i);
      const columnIdentifier = originalMatch![2].trim();
      const searchText = originalMatch![4].trim();
      const searchAllPages = /across\s+all\s+pages/i.test(original);
      
      return {
        action: 'verifyTableNotInColumn',
        selector: {
          tableValidation: 'notInColumn',
          columnIdentifier,
          searchAllPages
        },
        value: searchText,
        description: original
      };
    }
    
    // Pattern 1: "Verify table contains <text>" - Check if any cell in the table contains the text
    // Support "across all pages" to enable automatic pagination
    if (normalized.match(/^(verify|check|assert)\s+table\s+contains\s+(.+?)(?:\s+across\s+all\s+pages)?$/)) {
      const originalMatch = original.match(/^(verify|check|assert)\s+table\s+contains\s+(.+?)(?:\s+across\s+all\s+pages)?$/i);
      const searchText = originalMatch![2].trim(); // Case-sensitive search text
      const searchAllPages = /across\s+all\s+pages/i.test(original);
      
      return {
        action: 'verifyTable',
        selector: { tableValidation: 'contains', searchAllPages },
        value: searchText,
        description: original
      };
    }
    
    // Pattern 2: "Verify row <N> column <M> equals <value>" or "Verify row <N> column <name> equals <value>"
    if (normalized.match(/^(verify|check|assert)\s+row\s+(\d+)\s+column\s+(.+?)\s+(equals|contains|is)\s+(.+)$/)) {
      const originalMatch = original.match(/^(verify|check|assert)\s+row\s+(\d+)\s+column\s+(.+?)\s+(equals|contains|is)\s+(.+)$/i);
      const rowIndex = parseInt(originalMatch![2]);
      const columnIdentifier = originalMatch![3].trim(); // Can be number or column name
      const operator = originalMatch![4].toLowerCase() as 'equals' | 'contains' | 'is';
      const expectedValue = originalMatch![5].trim(); // Case-sensitive value
      
      return {
        action: 'verifyTableCell',
        selector: {
          rowIndex,
          columnIdentifier,
          operator
        },
        value: expectedValue,
        description: original
      };
    }
    
    // Pattern 3: "Verify table has <N> rows/items" or "Verify total <N> items/rows"
    // Support "across all pages" to count rows across pagination
    if (normalized.match(/^(verify|check|assert)\s+(?:the\s+)?(?:table\s+has|total)\s+(\d+)\s+(rows?|items?)(?:\s+across\s+all\s+pages)?$/)) {
      const match = normalized.match(/^(verify|check|assert)\s+(?:the\s+)?(?:table\s+has|total)\s+(\d+)\s+(rows?|items?)(?:\s+across\s+all\s+pages)?$/);
      const expectedRows = parseInt(match![2]);
      const searchAllPages = /across\s+all\s+pages/i.test(original);
      
      return {
        action: 'verifyTableRows',
        selector: { tableValidation: 'rowCount', searchAllPages },
        value: expectedRows.toString(),
        description: original
      };
    }
    
    // Pattern 4a: "Verify ALL records in table in <column> column have/has <value>" - Validate EVERY row in column (with explicit "table")
    if (normalized.match(/^(verify|check|assert)\s+(all|every)\s+records?\s+in\s+table\s+in\s+(.+?)\s+column\s+(have?|has|contains?)\s+(.+?)(?:\s+across\s+all\s+pages)?$/)) {
      const originalMatch = original.match(/^(verify|check|assert)\s+(all|every)\s+records?\s+in\s+table\s+in\s+(.+?)\s+column\s+(have?|has|contains?)\s+(.+?)(?:\s+across\s+all\s+pages)?$/i);
      const columnIdentifier = originalMatch![3].trim();
      const searchText = originalMatch![5].trim();
      const searchAllPages = /across\s+all\s+pages/i.test(original);
      
      return {
        action: 'verifyTableColumn',
        selector: { columnIdentifier, searchAllPages, validateAllRecords: true },
        value: searchText,
        description: original
      };
    }

    // Pattern 4b: "Verify ALL records in <column> column have/has <value>" - Validate EVERY row in column (original format)
    // This checks ALL rows, not just first match
    if (normalized.match(/^(verify|check|assert)\s+(all|every)\s+records?\s+in\s+(.+?)\s+column\s+(have?|has|contains?)\s+(.+?)(?:\s+across\s+all\s+pages)?$/)) {
      const originalMatch = original.match(/^(verify|check|assert)\s+(all|every)\s+records?\s+in\s+(.+?)\s+column\s+(have?|has|contains?)\s+(.+?)(?:\s+across\s+all\s+pages)?$/i);
      const columnIdentifier = originalMatch![3].trim();
      const searchText = originalMatch![5].trim();
      const searchAllPages = /across\s+all\s+pages/i.test(original);
      
      return {
        action: 'verifyTableColumn',
        selector: { columnIdentifier, searchAllPages, validateAllRecords: true },
        value: searchText,
        description: original
      };
    }

    // Pattern 5: "Verify table columns: A=X, B=Y, C=Z" - Multi-column validation with operator support
    // Supports operators: equals (=), contains, not equals, not contains, greater than, less than, starts with, ends with, is empty, is not empty
    // Examples:
    // - "Verify table columns: Name=John, Role not equals Admin"
    // - "Verify table columns: Status contains Active, Balance greater than 1000"
    // - "Verify table columns: Email is not empty, Description is empty"
    if (normalized.match(/^(verify|check|assert)\s+table\s+columns?\s*:\s*.+/)) {
      const originalMatch = original.match(/^(verify|check|assert)\s+table\s+columns?\s*:\s*(.+?)(?:\s+across\s+all\s+pages)?$/i);
      if (originalMatch) {
        const columnsText = originalMatch[2].trim();
        const searchAllPages = /across\s+all\s+pages/i.test(original);
        
        // Parse column-operator-value pairs
        type ColumnPair = {
          column: string;
          value: string;
          operator?: 'equals' | 'contains' | 'notEquals' | 'notContains' | 'greaterThan' | 'lessThan' | 'startsWith' | 'endsWith' | 'isEmpty' | 'isNotEmpty';
        };
        const columnPairs: ColumnPair[] = [];
        const pairs = columnsText.split(',');
        
        for (const pair of pairs) {
          const trimmedPair = pair.trim();
          let column = '';
          let value = '';
          let operator: ColumnPair['operator'] = 'equals';
          
          // Check for operator keywords (case insensitive)
          if (/\s+(not\s+equals?|!=)\s+/i.test(trimmedPair)) {
            const match = trimmedPair.match(/^(.+?)\s+(?:not\s+equals?|!=)\s+(.+)$/i);
            if (match) {
              column = match[1].trim();
              value = match[2].trim();
              operator = 'notEquals';
            }
          } else if (/\s+not\s+contains?\s+/i.test(trimmedPair)) {
            const match = trimmedPair.match(/^(.+?)\s+not\s+contains?\s+(.+)$/i);
            if (match) {
              column = match[1].trim();
              value = match[2].trim();
              operator = 'notContains';
            }
          } else if (/\s+contains?\s+/i.test(trimmedPair)) {
            const match = trimmedPair.match(/^(.+?)\s+contains?\s+(.+)$/i);
            if (match) {
              column = match[1].trim();
              value = match[2].trim();
              operator = 'contains';
            }
          } else if (/\s+(?:greater\s+than|>)\s+/i.test(trimmedPair)) {
            const match = trimmedPair.match(/^(.+?)\s+(?:greater\s+than|>)\s+(.+)$/i);
            if (match) {
              column = match[1].trim();
              value = match[2].trim();
              operator = 'greaterThan';
            }
          } else if (/\s+(?:less\s+than|<)\s+/i.test(trimmedPair)) {
            const match = trimmedPair.match(/^(.+?)\s+(?:less\s+than|<)\s+(.+)$/i);
            if (match) {
              column = match[1].trim();
              value = match[2].trim();
              operator = 'lessThan';
            }
          } else if (/\s+starts?\s+with\s+/i.test(trimmedPair)) {
            const match = trimmedPair.match(/^(.+?)\s+starts?\s+with\s+(.+)$/i);
            if (match) {
              column = match[1].trim();
              value = match[2].trim();
              operator = 'startsWith';
            }
          } else if (/\s+ends?\s+with\s+/i.test(trimmedPair)) {
            const match = trimmedPair.match(/^(.+?)\s+ends?\s+with\s+(.+)$/i);
            if (match) {
              column = match[1].trim();
              value = match[2].trim();
              operator = 'endsWith';
            }
          } else if (/\s+is\s+not\s+empty$/i.test(trimmedPair)) {
            const match = trimmedPair.match(/^(.+?)\s+is\s+not\s+empty$/i);
            if (match) {
              column = match[1].trim();
              value = ''; // No value needed for isEmpty/isNotEmpty
              operator = 'isNotEmpty';
            }
          } else if (/\s+is\s+empty$/i.test(trimmedPair)) {
            const match = trimmedPair.match(/^(.+?)\s+is\s+empty$/i);
            if (match) {
              column = match[1].trim();
              value = ''; // No value needed for isEmpty/isNotEmpty
              operator = 'isEmpty';
            }
          } else {
            // Default: equals (A=X or A equals X)
            const equalIndex = trimmedPair.indexOf('=');
            if (equalIndex > 0) {
              column = trimmedPair.substring(0, equalIndex).trim();
              value = trimmedPair.substring(equalIndex + 1).trim();
              operator = 'equals';
            } else if (/\s+equals?\s+/i.test(trimmedPair)) {
              const match = trimmedPair.match(/^(.+?)\s+equals?\s+(.+)$/i);
              if (match) {
                column = match[1].trim();
                value = match[2].trim();
                operator = 'equals';
              }
            }
          }
          
          if (column) {
            columnPairs.push({ column, value, operator });
          }
        }
        
        if (columnPairs.length > 0) {
          return {
            action: 'verifyTableColumns', // New action for multi-column
            selector: { 
              columnPairs, 
              searchAllPages, 
              validateAllRecords: true // Always validate all records for multi-column
            },
            value: '', // Not used for multi-column
            description: original
          };
        }
      }
    }
    
    // Pattern 4b: "Verify column <name> contains/has <value>" - Check if any cell in a specific column contains the value (LEGACY)
    // Support both "Verify column X contains Y" and "Verify X column has Y"
    // Support "across all pages" to enable automatic pagination
    if (normalized.match(/^(verify|check|assert)\s+(.+?)\s+column\s+(has|contains)\s+(.+?)(?:\s+across\s+all\s+pages)?$/)) {
      const originalMatch = original.match(/^(verify|check|assert)\s+(.+?)\s+column\s+(has|contains)\s+(.+?)(?:\s+across\s+all\s+pages)?$/i);
      const columnIdentifier = originalMatch![2].trim();
      const searchText = originalMatch![4].trim();
      const searchAllPages = /across\s+all\s+pages/i.test(original);
      
      return {
        action: 'verifyTableColumn',
        selector: { columnIdentifier, searchAllPages, validateAllRecords: false },
        value: searchText,
        description: original
      };
    }
    
    if (normalized.match(/^(verify|check|assert)\s+column\s+(.+?)\s+(contains|has)\s+(.+?)(?:\s+across\s+all\s+pages)?$/)) {
      const originalMatch = original.match(/^(verify|check|assert)\s+column\s+(.+?)\s+(contains|has)\s+(.+?)(?:\s+across\s+all\s+pages)?$/i);
      const columnIdentifier = originalMatch![2].trim();
      const searchText = originalMatch![4].trim();
      const searchAllPages = /across\s+all\s+pages/i.test(original);
      
      return {
        action: 'verifyTableColumn',
        selector: { columnIdentifier, searchAllPages, validateAllRecords: false },
        value: searchText,
        description: original
      };
    }
    
    return null;
  }

  /**
   * Parse multi-step scenario from multi-line input
   * Each line is a separate step, # lines are comments
   * @param input - Multi-line string with test steps
   * @param stepMetadata - Optional array of step metadata containing UUIDs and recorded step info
   */
  parseMultiStepScenario(input: string, stepMetadata?: StepMetadata[]): TestStep[] {
    const lines = input.split('\n')
      .map((line, index) => ({ line: line.trim(), lineNumber: index + 1, index }))
      .filter(({ line }) => line.length > 0 && !line.startsWith('#'));

    const steps: TestStep[] = [];

    for (const { line, lineNumber, index } of lines) {
      let step: TestStep | null = null;

      // Check if we have metadata for this step
      const metadata = stepMetadata && stepMetadata.length > 0 ? stepMetadata[index] : null;

      // If this is a recorded step, construct TestStep from recorded data
      // Only treat as recorded if artifacts are present (action or non-empty selector)
      if (metadata && metadata.additionalInfo?.recordedStep) {
        const recordedData = metadata.additionalInfo;
        const selectorHasKeys = !!(recordedData.selector && typeof recordedData.selector === 'object' && Object.keys(recordedData.selector).length > 0);
        const actionDefined = !!recordedData.action;
        const hasArtifacts = !!(actionDefined || selectorHasKeys);
        if (hasArtifacts) {
          step = {
            id: `step-${lineNumber}`,
            stepUuid: metadata.stepUuid,
            action: recordedData.action,
            selector: recordedData.selector || {},
            value: recordedData.value,
            text: recordedData.text,
            timeout: recordedData.timeout || 10000,
            description: metadata.stepDescription,
            displayValue: recordedData.displayValue
          };
        }
      }

      if (!step) {
        // Parse as natural language step
        step = this.parseNaturalLanguageStep(line, metadata);
        if (step) {
          step.id = `step-${lineNumber}`;
          step.description = line;

          // Add UUID if available
          if (metadata) {
            step.stepUuid = metadata.stepUuid;
            // Value recovery is now handled in parseNaturalLanguageStep
          }
        }
      }

      if (step) {
        steps.push(step);
      } else {
        // Parsing failed for this line, but do not log to avoid clutter
      }
    }

    return steps;
  }

  /**
   * If a step lacks an action (e.g., due to metadata issues), attempt to repair
   * it from its description using the natural language parser.
   */
  // private repairStepFromDescription(original: TestStep): TestStep {
  //   if (original && original.action) return original;
  //   const desc = (original?.description || '').trim();
  //   const parsed = desc ? this.parseNaturalLanguageStep(desc) : null;
  //   if (!parsed) return original;
  //   // Preserve identifiers and values from original when present
  //   const repaired: TestStep = {
  //     id: original.id,
  //     stepUuid: original.stepUuid,
  //     action: parsed.action,
  //     selector: Object.keys(original.selector || {}).length ? (original.selector as any) : parsed.selector,
  //     value: original.value !== undefined ? original.value : parsed.value,
  //     text: original.text || parsed.text,
  //     timeout: original.timeout || parsed.timeout,
  //     description: original.description || desc,
  //     displayValue: original.displayValue
  //   };
  //   return repaired;
  // }

  private shouldStop = false;

  /**
   * Execute a multi-step scenario sequentially
   * @param input - Multi-line string with test steps
   * @param options - Execution options
   */
  async executeScenario(
    input: string,
    options?: {
      stopOnFailure?: boolean;
      delayBetweenSteps?: number;
      navigationDelay?: number;
      onProgress?: (current: number, total: number) => void;
      stepMetadata?: StepMetadata[];  // Optional step metadata with UUIDs
      session?: any;  // Explicit session for parallel execution (bypasses singleton currentSession)
      executionContext?: { functionalAreaId?: string; scenarioId?: string; clientPackageMappingId?: string; executionRunId?: string; tenantId?: string };
    }
  ): Promise<ScenarioExecutionReport> {
    
    const steps = this.parseMultiStepScenario(input, options?.stepMetadata);

    // Count only real test steps (exclude wait/delay steps)
    const realStepCount = steps.filter(s => s.action !== 'wait').length;

    const report: ScenarioExecutionReport = {
      totalSteps: realStepCount,
      passed: 0,
      failed: 0,
      stopped: 0,
      duration: 0,
      steps: []
    };
    // Track capture/save operations to ensure completion before teardown
    const capturePromises: Promise<void>[] = [];
    
    if (steps.length === 0) {
      return report;
    }
    
    const startTime = Date.now();
    this.shouldStop = false;
    const stopOnFailure = options?.stopOnFailure !== false;
    const defaultDelay = options?.delayBetweenSteps || 3000; // 3 seconds default
    const navDelay = options?.navigationDelay || 5000; // 5 seconds for navigation
    
    for (let i = 0; i < steps.length; i++) {
      if (this.shouldStop) {
        // Mark remaining real steps as stopped (exclude wait steps)
        for (let j = i; j < steps.length; j++) {
          if (steps[j].action === 'wait') continue;
          report.stopped++;
          const stoppedStep = steps[j];
          const stoppedExecution: StepExecution = {
            step: stoppedStep,
            status: 'stopped',
            result: {
              success: false,
              error: 'Stopped',
              timestamp: new Date().toISOString()
            }
          };
          report.steps.push(stoppedExecution);
          // Persist stopped result as well (no screenshot expected)
          this.captureAndSaveResult(stoppedStep, stoppedExecution, undefined, options?.executionContext).catch(() => {});
          // report.steps.push(skippedExecution);
          // // Persist skipped result as well (no screenshot expected)
          // this.captureAndSaveResult(skippedStep, skippedExecution, undefined, options?.executionContext).catch(() => {});
        }
        break;
      }
      
      let step = steps[i];
      // Safety net: ensure step has action
      // if (!step.action) {
      //   step = this.repairStepFromDescription(step);
      // }

      // Wait/delay steps: execute the delay but don't count in report
      if (step.action === 'wait') {
        const waitMs = parseInt(step.value || '0') || 0;
        if (waitMs > 0) await this.delay(waitMs);
        continue;
      }

      // Small delay between steps to let DOM settle
      if (i > 0) {
        await this.delay(defaultDelay);
      }
      
      // Update progress
      if (options?.onProgress) {
        options.onProgress(i, steps.length);
      }
      
      // Mark as running
      const execution: StepExecution = {
        step,
        status: 'running'
      };
      const history = this.executionHistory$.value;
      this.executionHistory$.next([...history, execution]);
      
      try {
        // Execute step (already properly constructed by parseMultiStepScenario)
        // Screenshot will be captured IN ELECTRON after element is highlighted
        const result = await this.executeStep(step, false, options?.session); // Don't add to history here, we manage it below
        
        // Update execution with result
        execution.result = result;
        
        // For verification/assertion actions, check 'passed' field
        // For regular actions, check 'success' field
        const isVerificationAction = step.action === 'verify' || step.action === 'assert' || 
                                     step.action === 'verifyNotExist' || step.action === 'assertNotExist' ||
                                     step.action === 'verifyTableColumn' || step.action === 'verifyTableColumns' ||
                                     step.action === 'verifyTableNotContains' || step.action === 'verifyTableNotInColumn';
        
        // Get passed status from result.passed or result.assertion.passed
        // Handle double nesting: result.result.assertion.passed (from executeStep wrapping)
        const actualResult = result.result || result;
        const assertion = actualResult.assertion;
        
        // CRITICAL FIX: Check actualResult.success (the actual execution result)
        // not result.success which might be a wrapper
        const actionSuccess = actualResult?.success ?? result?.success ?? false;
        
        const verificationPassed = result.passed !== undefined 
          ? result.passed 
          : (assertion?.passed !== undefined ? assertion.passed : true);
        
        const stepPassed = isVerificationAction 
          ? verificationPassed  // For verifications, check passed field from result or assertion
          : actionSuccess;      // For regular actions, use actualResult.success
        
        if (stepPassed) {
          report.passed++;
          execution.status = 'success';
          report.steps.push(execution);
        } else {
          report.failed++;
          execution.status = 'failed';
          report.steps.push(execution);
        }
        
        // Capture screenshot and save to execution results (async, don't block)
        // IMPORTANT: This must be called BEFORE the stopOnFailure break to ensure failed steps are tracked
        // Use screenshot from execution result (captured in Electron after element highlighting)
        const screenshotFromResult = result?.result?.screenshot || result?.screenshot;
        capturePromises.push(this.captureAndSaveResult(step, execution, screenshotFromResult, options?.executionContext));
        
        // Check if we should stop on failure AFTER saving the result
        // IMPORTANT: verify failures should NOT stop execution, only assert failures should stop
        // IMPORTANT: optional steps should NOT stop execution even if they fail
        const isOptionalStep = step.optional === true;
        const isAssertAction = step.action === 'assert' || step.action === 'assertNotExist';
        if (!stepPassed && stopOnFailure && isAssertAction && !isOptionalStep) {
          // Mark remaining steps as skipped
          for (let j = i + 1; j < steps.length; j++) {
            if (steps[j].action === 'wait') continue;
            report.stopped++;
            const stoppedStep = steps[j];
            const stoppedExecution: StepExecution = {
              step: stoppedStep,
              status: 'stopped',
              result: {
                success: false,
                error: 'Stopped after failure',
                timestamp: new Date().toISOString()
              }
            };
            report.steps.push(stoppedExecution);
            // Persist stopped result as well
            capturePromises.push(this.captureAndSaveResult(stoppedStep, stoppedExecution,  undefined, options?.executionContext));
            // report.steps.push(skippedExecution);
            // // Persist skipped result as well
            // capturePromises.push(this.captureAndSaveResult(skippedStep, skippedExecution, undefined, options?.executionContext));
          }
          break;
        }
        
        // Trigger change detection by emitting a new array
        this.executionHistory$.next([...this.executionHistory$.value]);
        
        // NO delay here - delay moved to beginning of loop to ensure screenshots capture settled DOM
        
      } catch (error: any) {
        report.failed++;
        execution.status = 'failed';
        execution.result = {
          success: false,
          error: error.message || 'Unknown error',
          timestamp: new Date().toISOString()
        };
        report.steps.push(execution);
        
        // Capture screenshot and save failed execution to results
        // Try to use screenshot from error result, or capture from session window as fallback
        let errorScreenshot: string | undefined;
        if (execution.result && typeof execution.result === 'object') {
          errorScreenshot = (execution.result as any).screenshot;
        }
        if (!errorScreenshot) {
          try {
            // In session mode, capture from the session window; otherwise use hidden host
            const activeSession = options?.session || this.currentSession;
            if (activeSession?.windowHandle) {
              const host: any = (window as any).host;
              if (host?.captureScreenshotInSession) {
                const ssResult = await host.captureScreenshotInSession(activeSession.windowHandle);
                errorScreenshot = ssResult?.screenshot || ssResult?.data || undefined;
              }
            }
            if (!errorScreenshot) {
              errorScreenshot = await this.executionResultService.captureScreenshot() ?? undefined;
            }
          } catch {}
        }
        capturePromises.push(this.captureAndSaveResult(step, execution, errorScreenshot, options?.executionContext));
        
        // Trigger change detection by emitting a new array
        this.executionHistory$.next([...this.executionHistory$.value]);
        
        // IMPORTANT: verify actions should continue even if they throw errors
        // Only assert actions and regular action errors should stop execution
        // IMPORTANT: optional steps should NOT stop execution
        const isOptionalStep = step.optional === true;
        const isVerifyAction = step.action === 'verify';
        const isCheckAction = step.action === 'check' || step.action === 'uncheck';
        if (stopOnFailure && !isVerifyAction && !isOptionalStep && !isCheckAction) {
          // Mark remaining as skipped
          for (let j = i + 1; j < steps.length; j++) {
            if (steps[j].action === 'wait') continue;
            report.stopped++;
            const stoppedStep = steps[j];
            const stoppedExecution: StepExecution = {
              step: stoppedStep,
              status: 'stopped',
              result: {
                success: false,
                error: 'Stopped after error',
                timestamp: new Date().toISOString()
              }
            };
            report.steps.push(stoppedExecution);
            // Persist stopped result as well
            capturePromises.push(this.captureAndSaveResult(stoppedStep, stoppedExecution, undefined, options?.executionContext));
            // report.steps.push(skippedExecution);
            // // Persist skipped result as well
            // capturePromises.push(this.captureAndSaveResult(skippedStep, skippedExecution, undefined, options?.executionContext));
          }
          break;
        }
      }
    }
    
    report.duration = Date.now() - startTime;
    
    // Ensure all capture/save operations complete before reporting done
    try {
      await Promise.all(capturePromises);
    } catch (e) {
      // Swallow capture errors to not block execution report
    }

    // CRITICAL: Wait for all pending HTTP saves to complete (especially important for runner mode)
    try {
      await this.executionResultService.waitForPendingSaves();
    } catch {}

    if (options?.onProgress) {
      options.onProgress(steps.length, steps.length);
    }
    
    return report;
  }

  /**
   * Stop ongoing scenario execution
   */
  stopExecution(): void {
    this.shouldStop = true;
  }

  /**
   * Execute a single test step on the browser
   * @param step - The step to execute
   * @param addToHistory - Whether to add this execution to history (default true)
   */
  async executeStep(step: TestStep, addToHistory: boolean = true, session?: any): Promise<ExecutionResult> {
    const startTime = Date.now();
    // Use provided step as-is; value contains the real value for execution
    // SAFETY NET: If action is missing, attempt to infer from description/value
    // if (!step.action) {
    //   const desc = (step.description || '').trim();
    //   const inferred = desc ? this.parseNaturalLanguageStep(desc) : null;
    //   if (inferred) {
    //     step.action = inferred.action;
    //     // If selector is empty, use inferred selector
    //     if (!step.selector || Object.keys(step.selector).length === 0) {
    //       step.selector = inferred.selector || {} as any;
    //     }
    //     // Preserve existing value; if missing, use inferred value
    //     if (step.value === undefined && inferred.value !== undefined) {
    //       step.value = inferred.value;
    //     }
    //     // Preserve text if missing
    //     if (!step.text && inferred.text) {
    //       step.text = inferred.text;
    //     }
    //   } else if (typeof step.value === 'string' && step.value.length > 0) {
    //     // Minimal fallback: default to 'fill' using a textbox selector inferred from description keywords
    //     step.action = 'fill' as any;
    //     const lowerDesc = desc.toLowerCase();
    //     // Try to extract field name from "enter <field> as <value>" or "enter <value> in <field>"
    //     let fieldName = '';
    //     const m1 = lowerDesc.match(/^(enter|type|fill|input)\s+(.+?)\s+as\s+(.+?)$/);
    //     const m2 = lowerDesc.match(/^(enter|type|fill|input)\s+(.+?)\s+(in|into)\s+(?:the\s+)?(.+?)$/);
    //     if (m1) {
    //       fieldName = m1[2].replace(/^\s*the\s+/, '').trim();
    //     } else if (m2) {
    //       fieldName = m2[4].replace(/^\s*the\s+/, '').trim();
    //     }
    //     const keyWord = fieldName ? fieldName.split(/\s+/)[0] : 'input';
    //     step.selector = step.selector && Object.keys(step.selector).length ? step.selector : ({ role: 'textbox', name: fieldName || keyWord, id: keyWord } as any);
    //   }
    // }
    this.currentStep$.next(step);
    
    try {
      const host: any = (window as any).host;
      
      // Check if we're using session windows or legacy hidden host
      const activeSession = session || this.currentSession;
      if (activeSession?.windowHandle) {
        // Session-based execution (NEW)
        console.log('[TestExecutor] Executing step in session window:', activeSession.windowHandle);
        
        if (!host?.executeStepInSession) {
          // Fallback: try to use step:execute IPC directly
          console.warn('[TestExecutor] executeStepInSession not available, using executeStep fallback');
          if (!host?.executeStep) {
            throw new Error('Electron host API not available');
          }
          const response = await host.executeStep(step);
          const duration = Date.now() - startTime;

          // Extract screenshot from response (could be at top level or nested in result)
          const screenshot = response.result?.screenshot || response.screenshot;

          const result: ExecutionResult = {
            success: response.success,
            result: response.result,
            error: response.error,
            timestamp: new Date().toISOString(),
            duration,
            screenshot  // Attach screenshot at top level for easy access
          };

          // If failed with suggestions, format helpful error
          if (!response.success && response.suggestions) {
            result.error = this.formatErrorWithSuggestions(response.error, response.suggestions);
          }

          // Add to history only if not called from executeScenario (to prevent duplicates)
          if (addToHistory) {
            const execution: StepExecution = {
              step,
              result,
              status: response.success ? 'success' : 'failed'
            };
            
            const history = this.executionHistory$.value;
            this.executionHistory$.next([...history, execution]);
          }

          this.currentStep$.next(null);
          return result;
        }
        
        // Use session-based execution
        // CRITICAL: Validate window handle is still valid before execution
        const windowHandle = activeSession.windowHandle;
        
        if (host.validateSessionWindow) {
          const validation = await host.validateSessionWindow(windowHandle);
          if (!validation?.valid) {
            const errorMsg = `Session window ${windowHandle} is destroyed or not found. Window was likely closed between scenarios.`;
            throw new Error(errorMsg);
          }
        }
        
        const response = await host.executeStepInSession(windowHandle, step);
        const duration = Date.now() - startTime;

        // Extract screenshot from response
        const screenshot = response.result?.screenshot || response.screenshot;

        const result: ExecutionResult = {
          success: response.success,
          result: response.result,
          error: response.error,
          timestamp: new Date().toISOString(),
          duration,
          screenshot
        };

        // If failed with suggestions, format helpful error
        if (!response.success && response.suggestions) {
          result.error = this.formatErrorWithSuggestions(response.error, response.suggestions);
        }

        // Add to history
        if (addToHistory) {
          const execution: StepExecution = {
            step,
            result,
            status: response.success ? 'success' : 'failed'
          };
          
          const history = this.executionHistory$.value;
          this.executionHistory$.next([...history, execution]);
        }

        this.currentStep$.next(null);
        return result;
      } else {
        // Legacy execution using hidden host (backward compatibility)
        if (!host?.executeStep) {
          throw new Error('Electron host API not available');
        }

        const response = await host.executeStep(step);
        const duration = Date.now() - startTime;

        // Extract screenshot from response (could be at top level or nested in result)
        const screenshot = response.result?.screenshot || response.screenshot;

        const result: ExecutionResult = {
          success: response.success,
          result: response.result,
          error: response.error,
          timestamp: new Date().toISOString(),
          duration,
          screenshot  // Attach screenshot at top level for easy access
        };

        // If failed with suggestions, format helpful error
        if (!response.success && response.suggestions) {
          result.error = this.formatErrorWithSuggestions(response.error, response.suggestions);
        }

        // Add to history only if not called from executeScenario (to prevent duplicates)
        if (addToHistory) {
          const execution: StepExecution = {
            step,
            result,
            status: response.success ? 'success' : 'failed'
          };
          
          const history = this.executionHistory$.value;
          this.executionHistory$.next([...history, execution]);
        }

        this.currentStep$.next(null);
        return result;
      }
      
    } catch (error: any) {
      const duration = Date.now() - startTime;
      const result: ExecutionResult = {
        success: false,
        error: error.message || 'Unknown error',
        timestamp: new Date().toISOString(),
        duration
      };

      const execution: StepExecution = {
        step,
        result,
        status: 'failed'
      };
      
      const history = this.executionHistory$.value;
      this.executionHistory$.next([...history, execution]);

      this.currentStep$.next(null);
      return result;
    }
  }

  // Removed runtime placeholder resolver and cache: steps carry real values now

  /**
   * Format error with helpful suggestions
   */
  private formatErrorWithSuggestions(error: string, suggestions: any[]): string {
    let message = error + '\n\nAvailable elements on page:\n';
    suggestions.slice(0, 5).forEach((elem, i) => {
      message += `\n${i + 1}. ${elem.type}: ${elem.identifiers.join(', ')}`;
    });
    message += '\n\nTry using one of these identifiers in your command.';
    return message;
  }

  /**
   * Execute a natural language step
   */
  async executeNaturalLanguageStep(input: string): Promise<ExecutionResult> {
    const step = this.parseNaturalLanguageStep(input);
    
    if (!step) {
      return {
        success: false,
        error: 'Could not parse step: ' + input,
        timestamp: new Date().toISOString()
      };
    }

    return this.executeStep(step);
  }

  /**
   * Execute multiple steps in sequence
   */
  async executeSteps(steps: TestStep[]): Promise<ExecutionResult[]> {
    const results: ExecutionResult[] = [];
    
    for (const step of steps) {
      const result = await this.executeStep(step);
      results.push(result);
      
      // Stop on first failure
      if (!result.success) {
        break;
      }
      
      // Small delay between steps
      await this.delay(500);
    }
    
    return results;
  }

  /**
   * Clear execution history
   */
  clearHistory(): void {
    this.executionHistory$.next([]);
  }

  /**
   * Check if the step involves a password field
   */
  private isPasswordField(step: TestStep): boolean {
    const selectorName = step.selector?.name?.toLowerCase() || '';
    const description = step.description?.toLowerCase() || '';
    const value = typeof step.value === 'string' ? step.value.toLowerCase() : '';
    
    // Check if any of these fields contain "password"
    return selectorName.includes('password') || 
           description.includes('password') ||
           value.includes('password');
  }

  /**
   * Mask sensitive information in description
   */
  private maskPasswordInDescription(description: string): string {
    // Pattern 1: "as/with/to <password>" - more specific to capture the value after these keywords
    description = description.replace(/\b(as|with|to)\s+([^\s]+)/gi, (match, prefix, value) => {
      return `${prefix} *******`;
    });
    
    // Pattern 2: If still contains password-like values, mask them directly
    // This handles cases like password patterns
    description = description.replace(/[A-Za-z][A-Za-z0-9@#$%^&*!]{5,}/g, (match) => {
      // Only mask if it looks like a password (contains letters and numbers/special chars)
      if (/[A-Za-z]/.test(match) && /[@#$%^&*!0-9]/.test(match)) {
        return '*******';
      }
      return match;
    });
    
    return description;
  }

  /**
   * Capture screenshot and save execution result
   */
  private async captureAndSaveResult(step: TestStep, execution: StepExecution, preExecutionScreenshot?: string, executionContext?: { functionalAreaId?: string; scenarioId?: string; clientPackageMappingId?: string; executionRunId?: string; tenantId?: string }): Promise<void> {
    // Use explicit context if provided (parallel mode), otherwise fall back to singleton
    let currentFunctionalAreaId: string | undefined;
    let currentScenarioId: string | undefined;
    let currentClientPackageMappingId: string | undefined;

    if (executionContext) {
      currentFunctionalAreaId = executionContext.functionalAreaId;
      currentScenarioId = executionContext.scenarioId;
      currentClientPackageMappingId = executionContext.clientPackageMappingId;
    } else {
      const context = this.executionResultService.getCurrentContext();
      currentFunctionalAreaId = context.packageId || undefined;
      currentScenarioId = context.scenarioId || undefined;
      currentClientPackageMappingId = context.clientPackageMappingId || undefined;
    }
        
    try {

      // ✅ If scenarioId is still missing, do NOT attempt DB save (it will be skipped anyway)
      if (!currentScenarioId) {
        return;
      }

      // Use pre-execution screenshot if provided, otherwise capture current state
      const screenshot = preExecutionScreenshot || await this.executionResultService.captureScreenshot();
      
      // Get passed status from result
      const actualResult = execution.result?.result || execution.result;
      const assertion = actualResult?.assertion;
      
      // For verification actions, use assertion.passed, for regular actions use status === 'success'
      const isVerificationAction = step.action === 'verify' || step.action === 'assert' || step.action === 'verifyTableColumn' || step.action === 'verifyTableColumns';
      let passed: boolean | undefined;
      
      if (isVerificationAction) {
        passed = assertion?.passed !== undefined ? assertion.passed : undefined;
      } else {
        // For regular actions (fill, click, etc.), set passed based on success status
        passed = execution.status === 'success';
      }
      
      // Map status to ExecutionResultRecord type (exclude 'pending')
      let recordStatus: 'success' | 'failed' | 'stopped' | 'running' = execution.status === 'pending' ? 'running' : execution.status;
      
      // Generate description
      let description = step.description || `${step.action} ${step.selector?.name || step.value || ''}`.trim();
      
      // Mask password if this is a password-related field
      if (this.isPasswordField(step)) {
        description = this.maskPasswordInDescription(description);
      }
      
      // Create execution result record
      const resultRecord: ExecutionResultRecord = {
        stepId: step.id || `step-${Date.now()}`,
        stepUuid: step.stepUuid, // Include stepUuid from TestStep
        action: step.action,
        description: description,
        status: recordStatus,
        passed: passed,
        result: execution.result,
        error: execution.result?.error,
        screenshot: screenshot || undefined,
        timestamp: execution.result?.timestamp || new Date().toISOString(),
        duration: execution.result?.duration,
        functionalAreaId: currentFunctionalAreaId,
        clientPackageMappingId: currentClientPackageMappingId, // Use extracted value
        scenarioId: currentScenarioId,
        executionRunId: executionContext?.executionRunId || undefined
      };
      
      
      // Save to execution result service
      // Use addResultWithContext when explicit context provided (parallel mode) to avoid singleton overwrite
      if (executionContext) {
        this.executionResultService.addResultWithContext(resultRecord, {
          functionalAreaId: currentFunctionalAreaId,
          scenarioId: currentScenarioId,
          clientPackageMappingId: currentClientPackageMappingId,
          executionRunId: executionContext?.executionRunId,
          tenantId: executionContext?.tenantId
        });
      } else {
        this.executionResultService.addResult(resultRecord);
      }
    } catch (error) {
      // Still try to save the result even if screenshot capture fails
      try {
        if (!currentScenarioId) return;
        const resultRecord: ExecutionResultRecord = {
          stepId: step.id || `step-${Date.now()}`,
          stepUuid: step.stepUuid,
          action: step.action,
          description: step.description || `${step.action} ${step.selector?.name || step.value || ''}`.trim(),
          status: execution.status === 'pending' ? 'running' : execution.status,
          passed: execution.status === 'success',
          result: execution.result,
          error: execution.result?.error,
          screenshot: undefined,
          timestamp: execution.result?.timestamp || new Date().toISOString(),
          duration: execution.result?.duration,
          functionalAreaId: currentFunctionalAreaId,
          clientPackageMappingId: currentClientPackageMappingId,
          scenarioId: currentScenarioId,
          executionRunId: executionContext?.executionRunId || undefined
        };
        if (executionContext) {
          this.executionResultService.addResultWithContext(resultRecord, {
            functionalAreaId: currentFunctionalAreaId,
            scenarioId: currentScenarioId,
            clientPackageMappingId: currentClientPackageMappingId,
            executionRunId: executionContext?.executionRunId,
            tenantId: executionContext?.tenantId
          });
        } else {
          this.executionResultService.addResult(resultRecord);
        }
      } catch (fallbackError) {
      }
    }
  }

  /**
   * Get page state from browser
   */
  async getPageState(): Promise<any> {
    if (!(window as any).host?.getPageState) {
      throw new Error('Electron host API not available');
    }
    
    const response = await (window as any).host.getPageState();
    return response.success ? response.state : null;
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}